import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenantSettings } from '../hooks/useTenantSettings';
import { useTier } from '../context/TierContext';
import { UpgradePrompt } from '../components/FeatureGate';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ImageLightbox } from '../components/ui/image-lightbox';
import { DatePicker } from '../components/ui/date-picker';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { SortableTableHead } from '../components/ui/sortable-table-head';
import { useSortableTable } from '../hooks/useSortableTable';
import useInfiniteList from '../hooks/useInfiniteList';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  FileText,
  Plus,
  Search,
  Download,
  ClipboardList,
  AlertTriangle,
  CheckCircle,
  User,
  Truck,
  Pencil,
  Trash2,
  FileCheck,
  Clock,
  Eye,
  ArrowUp,
  ArrowDown,
  GripVertical,
  CornerDownRight,
  Copy,
  History,
  Save,
} from 'lucide-react';
import HireInspectionTemplatesTab from '../components/HireInspectionTemplatesTab';
import { CONDITION_OPERATORS } from '../lib/formLogic';

const Forms = () => {
  const { api, user } = useAuth();
  const { fmtDateTime } = useTenantSettings();
  const { hasTierFeature } = useTier();
  const isAdmin = user?.role === 'admin';
  // Custom Forms Builder access is granted by the real `forms` feature, which
  // is present on Advanced/Professional tiers AND can be granted to any tier via
  // a super-admin feature override. (The old `custom_forms` key was never a real
  // grantable feature, so overrides could not unlock it.)
  const hasCustomForms = hasTierFeature('forms');
  
  const [templates, setTemplates] = useState([]);
  const [equipmentTypesList, setEquipmentTypesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('submissions');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showInactive, setShowInactive] = useState(true);
  const [subStats, setSubStats] = useState({ total: 0, job_linked: 0, incident: 0 });
  const submissionsList = useInfiniteList({
    endpoint: '/form-submissions/paginated',
    search: searchTerm,
    params: { form_type: filterType === 'all' ? '' : filterType },
  });
  const submissions = submissionsList.items;
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [editingSubmission, setEditingSubmission] = useState(false);
  const [editData, setEditData] = useState({});
  const [editSubNote, setEditSubNote] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Template form state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    form_type: 'job_linked',
    fields: [],
    is_active: true,
    is_mandatory: false,
    include_in_docket: false,
    allow_multiple_submissions: true,
    equipment_types: [],
    requires_approval: false,
    approval_rules: [],
    approval_message: '',
    allow_edit_after_submission: false
  });

  // Drag-and-drop reorder state for Form Fields
  const [dragFieldIndex, setDragFieldIndex] = useState(null);
  const [dragOverFieldIndex, setDragOverFieldIndex] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [templatesRes, eqTypesRes, statsRes] = await Promise.all([
        api.get('/form-templates?include_inactive=true'),
        api.get('/equipment-types').catch(() => ({ data: [] })),
        api.get('/form-submissions/stats').catch(() => ({ data: { total: 0, job_linked: 0, incident: 0 } })),
      ]);
      setTemplates(templatesRes.data);
      setSubStats(statsRes.data || { total: 0, job_linked: 0, incident: 0 });
      setEquipmentTypesList(
        (eqTypesRes.data || []).map(t => typeof t === 'string' ? t : t.name).filter(Boolean).sort()
      );
    } catch (error) {
      toast.error('Failed to fetch forms data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewSubmission = async (submission) => {
    try {
      const res = await api.get(`/form-submissions/${submission.id}`);
      setSelectedSubmission(res.data);
      setEditingSubmission(false);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load submission details');
    }
  };

  const handleDownloadSubmissionPDF = async (submission) => {
    try {
      const response = await api.get(`/form-submissions/${submission.id}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${(submission.template_name || 'form').replace(/\s+/g, '_')}_${submission.id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleOpenTemplateDialog = (template = null) => {
    if (template) {
      setSelectedTemplate(template);
      setTemplateForm({
        name: template.name,
        description: template.description || '',
        form_type: template.form_type,
        fields: template.fields || [],
        is_active: template.is_active,
        is_mandatory: template.is_mandatory || false,
        include_in_docket: template.include_in_docket || false,
        allow_multiple_submissions: template.allow_multiple_submissions !== false,
        equipment_types: template.equipment_types || [],
        requires_approval: template.requires_approval || false,
        approval_rules: template.approval_rules || [],
        approval_message: template.approval_message || '',
        allow_edit_after_submission: template.allow_edit_after_submission || false
      });
    } else {
      setSelectedTemplate(null);
      setTemplateForm({
        name: '',
        description: '',
        form_type: 'job_linked',
        fields: [],
        is_active: true,
        is_mandatory: false,
        include_in_docket: false,
        allow_multiple_submissions: true,
        equipment_types: [],
        requires_approval: false,
        approval_rules: [],
        approval_message: '',
        allow_edit_after_submission: false
      });
    }
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    try {
      if (selectedTemplate && !selectedTemplate.is_system) {
        await api.put(`/form-templates/${selectedTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await api.post('/form-templates', templateForm);
        toast.success('Template created');
      }
      setTemplateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  // Duplicate a whole template — opens the create dialog pre-filled with a clone.
  const handleDuplicateTemplate = (template) => {
    const clonedFields = (template.fields || []).map((f, i) => {
      const clone = JSON.parse(JSON.stringify(f));
      clone.id = `field_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 5)}`;
      if (Array.isArray(clone.columns)) {
        clone.columns = clone.columns.map((c, ci) => ({ ...c, id: `col_${Date.now()}_${i}_${ci}` }));
      }
      return clone;
    });
    setSelectedTemplate(null); // save as NEW (POST)
    setTemplateForm({
      name: `${template.name} (Copy)`,
      description: template.description || '',
      form_type: template.form_type,
      fields: clonedFields,
      is_active: true,
      is_mandatory: template.is_mandatory || false,
      include_in_docket: template.include_in_docket || false,
      allow_multiple_submissions: template.allow_multiple_submissions !== false,
      equipment_types: template.equipment_types || [],
      requires_approval: template.requires_approval || false,
      approval_rules: template.approval_rules || [],
      approval_message: template.approval_message || '',
      allow_edit_after_submission: template.allow_edit_after_submission || false
    });
    setTemplateDialogOpen(true);
    toast.info('Reviewing a copy — adjust and save to create the new template');
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await api.delete(`/form-templates/${templateId}`);
      toast.success('Template deleted');
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleToggleActive = async (template) => {
    const next = !template.is_active;
    // optimistic update
    setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, is_active: next } : t));
    try {
      await api.patch(`/form-templates/${template.id}/active`, { is_active: next });
      toast.success(next ? `"${template.name}" activated` : `"${template.name}" deactivated`);
    } catch (error) {
      // revert on failure
      setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, is_active: !next } : t));
      toast.error(error.response?.data?.detail || 'Failed to update form status');
    }
  };

  const addField = () => {
    setTemplateForm({
      ...templateForm,
      fields: [
        ...templateForm.fields,
        {
          id: `field_${Date.now()}`,
          type: 'text',
          label: '',
          required: false,
          options: [],
          placeholder: ''
        }
      ]
    });
  };

  const updateField = (index, updates) => {
    const newFields = [...templateForm.fields];
    newFields[index] = { ...newFields[index], ...updates };
    setTemplateForm({ ...templateForm, fields: newFields });
  };

  const _newBlankField = () => ({
    id: `field_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    type: 'text',
    label: '',
    required: false,
    options: [],
    placeholder: ''
  });

  // Insert a new blank field directly after the given index.
  const insertFieldAfter = (index) => {
    setTemplateForm(prev => {
      const fields = [...prev.fields];
      fields.splice(index + 1, 0, _newBlankField());
      return { ...prev, fields };
    });
  };

  // Duplicate a field (deep clone with a fresh id) and insert it right after.
  const duplicateField = (index) => {
    setTemplateForm(prev => {
      const fields = [...prev.fields];
      const src = fields[index];
      if (!src) return prev;
      const clone = JSON.parse(JSON.stringify(src));
      clone.id = `field_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      if (clone.label) clone.label = `${clone.label} (copy)`;
      // give nested repeating-group columns fresh ids
      if (Array.isArray(clone.columns)) {
        clone.columns = clone.columns.map((c, i) => ({ ...c, id: `col_${Date.now()}_${i}` }));
      }
      fields.splice(index + 1, 0, clone);
      return { ...prev, fields };
    });
  };

  // Move a field up or down in the order.
  const moveField = (index, direction) => {
    setTemplateForm(prev => {
      const fields = [...prev.fields];
      const target = index + direction;
      if (target < 0 || target >= fields.length) return prev;
      [fields[index], fields[target]] = [fields[target], fields[index]];
      return { ...prev, fields };
    });
  };

  // Drag-and-drop reordering.
  const handleFieldDrop = (targetIndex) => {
    setTemplateForm(prev => {
      if (dragFieldIndex === null || dragFieldIndex === targetIndex) return prev;
      const fields = [...prev.fields];
      const [moved] = fields.splice(dragFieldIndex, 1);
      const insertAt = dragFieldIndex < targetIndex ? targetIndex - 1 : targetIndex;
      fields.splice(insertAt, 0, moved);
      return { ...prev, fields };
    });
    setDragFieldIndex(null);
    setDragOverFieldIndex(null);
  };


  const handleSectionImageUpload = async (index, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/form-submissions/upload-photo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateField(index, { image_url: res.data.url });
      toast.success('Image uploaded');
    } catch (e) {
      toast.error('Failed to upload image');
    }
  };

  const updateColumn = (fieldIndex, colIndex, updates) => {
    const cols = [...(templateForm.fields[fieldIndex].columns || [])];
    cols[colIndex] = { ...cols[colIndex], ...updates };
    updateField(fieldIndex, { columns: cols });
  };
  const addColumn = (fieldIndex) => {
    const cols = [...(templateForm.fields[fieldIndex].columns || [])];
    cols.push({ id: `col_${Date.now()}`, label: '', type: 'text', options: [] });
    updateField(fieldIndex, { columns: cols });
  };
  const removeColumn = (fieldIndex, colIndex) => {
    const cols = [...(templateForm.fields[fieldIndex].columns || [])].filter((_, i) => i !== colIndex);
    updateField(fieldIndex, { columns: cols });
  };

  const removeField = (index) => {
    setTemplateForm({
      ...templateForm,
      fields: templateForm.fields.filter((_, i) => i !== index)
    });
  };

  // ---- Conditional visibility (show_if) editor helpers ----
  const setFieldShowIf = (index, showIf) => updateField(index, { show_if: showIf });

  // ---- Approval-rule builder helpers ----
  const addApprovalRule = () => {
    setTemplateForm(prev => ({
      ...prev,
      approval_rules: [...(prev.approval_rules || []), { field: '', op: 'equals', value: '' }]
    }));
  };
  const updateApprovalRule = (i, updates) => {
    setTemplateForm(prev => {
      const rules = [...(prev.approval_rules || [])];
      rules[i] = { ...rules[i], ...updates };
      return { ...prev, approval_rules: rules };
    });
  };
  const removeApprovalRule = (i) => {
    setTemplateForm(prev => ({
      ...prev,
      approval_rules: (prev.approval_rules || []).filter((_, idx) => idx !== i)
    }));
  };

  // ---- Submission approval actions ----
  const applySubmissionUpdate = (updated) => {
    submissionsList.reload();
    api.get('/form-submissions/stats').then(r => setSubStats(r.data)).catch(() => {});
    setSelectedSubmission(prev => (prev && prev.id === updated.id) ? { ...prev, ...updated } : prev);
  };
  const handleApproveSubmission = async (submission) => {
    try {
      const res = await api.post(`/form-submissions/${submission.id}/approve`);
      applySubmissionUpdate(res.data);
      toast.success('Submission approved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };
  const handleRejectSubmission = async (submission) => {
    const reason = window.prompt('Reason for rejection (optional):', '') ?? '';
    try {
      const res = await api.post(`/form-submissions/${submission.id}/reject`, { reason });
      applySubmissionUpdate(res.data);
      toast.success('Submission rejected');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const getFieldTypeLabel = (type) => {
    const types = {
      text: 'Text',
      number: 'Number',
      textarea: 'Long Text',
      checkbox: 'Checkbox',
      select: 'Dropdown',
      date: 'Date',
      yes_no: 'Yes/No',
      signature: 'Signature',
      photo: 'Photo',
      multi_photo: 'Multiple Photos',
      multi_select: 'Checklist',
      section: 'Section / Info',
      repeating_group: 'Repeating Table'
    };
    return types[type] || type;
  };

  const renderFieldValue = (field, value) => {
    if (value === undefined || value === null || value === '') {
      return <span className="text-muted-foreground">-</span>;
    }
    
    switch (field?.type) {
      case 'yes_no':
        return value === true || value === 'yes' || value === 'Yes' ? (
          <Badge variant="outline" className="bg-green-500/20 text-green-500">Yes</Badge>
        ) : (
          <Badge variant="outline" className="bg-red-500/20 text-red-500">No</Badge>
        );
      case 'checkbox':
        return value ? <CheckCircle className="h-4 w-4 text-green-500" /> : <span>-</span>;
      case 'photo':
        return value ? (
          <button type="button" onClick={() => setLightboxUrl(value)} className="text-blue-500 hover:underline cursor-zoom-in">
            View Photo
          </button>
        ) : '-';
      case 'multi_photo':
        return Array.isArray(value) && value.length > 0 ? (
          <div className="flex flex-wrap gap-1 justify-end">
            {value.map((photo, idx) => (
              <button key={`mp-${idx}`} type="button" onClick={() => setLightboxUrl(photo)} className="cursor-zoom-in">
                <img src={photo} alt={`Photo ${idx + 1}`} className="h-12 w-12 object-cover rounded border hover:opacity-80" />
              </button>
            ))}
          </div>
        ) : '-';
      case 'signature':
        return value ? (
          <button type="button" onClick={() => setLightboxUrl(value)} className="cursor-zoom-in">
            <img src={value} alt="Signature" className="h-12 border rounded hover:opacity-80" />
          </button>
        ) : '-';
      default:
        return <span>{String(value)}</span>;
    }
  };

  // Editable input for a field (used when editing a submitted form). Media &
  // repeating groups stay read-only in this first version.
  const renderFieldInput = (field, value, onChange) => {
    switch (field?.type) {
      case 'textarea':
        return <Textarea value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={`edit-field-${field.id}`} />;
      case 'number':
        return <Input type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} data-testid={`edit-field-${field.id}`} />;
      case 'date':
        return <DatePicker value={value ?? ''} onChange={onChange} testId={`edit-field-${field.id}`} />;
      case 'yes_no':
        return (
          <Select
            value={value === true || value === 'yes' || value === 'Yes' ? 'yes' : (value === false || value === 'no' || value === 'No' ? 'no' : '')}
            onValueChange={(v) => onChange(v === 'yes')}
          >
            <SelectTrigger className="w-32" data-testid={`edit-field-${field.id}`}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
          </Select>
        );
      case 'checkbox':
        return <Switch checked={!!value} onCheckedChange={onChange} data-testid={`edit-field-${field.id}`} />;
      case 'select':
        return (
          <Select value={value ?? ''} onValueChange={onChange}>
            <SelectTrigger data-testid={`edit-field-${field.id}`}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{(field.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        );
      case 'multi_select': {
        const arr = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-1">
            {(field.options || []).map(o => (
              <label key={o} className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="accent-[#ffd108]" checked={arr.includes(o)}
                  onChange={(e) => onChange(e.target.checked ? [...arr, o] : arr.filter(x => x !== o))} />
                {o}
              </label>
            ))}
          </div>
        );
      }
      case 'photo':
      case 'multi_photo':
      case 'signature':
      case 'repeating_group':
        return (
          <span className="text-xs text-muted-foreground italic">
            {renderFieldValue(field, value)} <span className="ml-1">(not editable here)</span>
          </span>
        );
      default:
        return <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={`edit-field-${field.id}`} />;
    }
  };

  const canEditSubmission = () =>
    (isAdmin || user?.role === 'dispatcher') && !!selectedSubmission?.template?.allow_edit_after_submission;

  const startEditSubmission = () => {
    setEditData({ ...(selectedSubmission?.data || {}) });
    setEditSubNote('');
    setEditingSubmission(true);
  };

  const saveSubmissionEdit = async () => {
    if (!selectedSubmission) return;
    setSavingEdit(true);
    try {
      const res = await api.put(`/form-submissions/${selectedSubmission.id}`, {
        data: editData,
        edit_note: editSubNote,
      });
      setSelectedSubmission(res.data);
      setEditingSubmission(false);
      toast.success('Submission updated — change logged for audit');
      submissionsList.reload();
      api.get('/form-submissions/stats').then(r => setSubStats(r.data)).catch(() => {});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update submission');
    } finally {
      setSavingEdit(false);
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    if (filterType === 'all') return true;
    const template = templates.find(t => t.id === sub.template_id);
    const subFormType = template?.form_type || 'job_linked';
    return subFormType === filterType;
  });

  // For Basic tier, only show system templates
  const displayedTemplates = hasCustomForms ? templates : templates.filter(t => t.is_system);

  const filteredTemplates = displayedTemplates.filter(t =>
    t.name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (showInactive || t.is_active)
  );
  const inactiveCount = displayedTemplates.filter(t => !t.is_active).length;

  const { sortedData: sortedSubmissions, requestSort: requestSubmissionSort, getSortDirection: getSubmissionSortDirection } = useSortableTable(filteredSubmissions, {
    form: (s) => (s.template_name || '').toLowerCase(),
    operator: (s) => (s.operator_name || '').toLowerCase(),
    asset: (s) => (s.equipment_name || s.job_description || '').toLowerCase(),
    submitted: 'submitted_at',
    type: (s) => (templates.find(t => t.id === s.template_id)?.form_type || 'job_linked'),
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return fmtDateTime(dateStr);
  };

  const renderApprovalBadge = (sub) => {
    if (!sub?.requires_approval) return null;
    const status = sub.approval_status || 'pending';
    const map = {
      pending: { cls: 'bg-amber-500/20 text-amber-600 border-amber-500/40', label: 'Awaiting Approval' },
      approved: { cls: 'bg-green-500/20 text-green-600 border-green-500/40', label: 'Approved' },
      rejected: { cls: 'bg-red-500/20 text-red-600 border-red-500/40', label: 'Rejected' },
    };
    const m = map[status] || map.pending;
    return (
      <Badge variant="outline" className={`text-[10px] ${m.cls}`} data-testid={`submission-approval-badge-${sub.id}`}>
        {m.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffd108]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-[#ffd108]" />
            Forms
          </h1>
          <p className="text-sm text-muted-foreground">
            {hasCustomForms 
              ? 'View submitted forms and manage templates'
              : 'View submitted forms and use system templates'
            }
          </p>
        </div>
      </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <FileText className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{subStats.total}</p>
                  <p className="text-xs text-muted-foreground">Total Submissions</p>
                </div>
              </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <FileCheck className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{subStats.job_linked}</p>
                <p className="text-xs text-muted-foreground">Job-Linked Forms</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{subStats.incident}</p>
                <p className="text-xs text-muted-foreground">Incident Reports</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <ClipboardList className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{displayedTemplates.length}</p>
                <p className="text-xs text-muted-foreground">Form Templates</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <TabsList>
            <TabsTrigger value="submissions">Submissions</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="hire-inspections" data-testid="tab-hire-inspections">Hire Inspections</TabsTrigger>
          </TabsList>
          
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-64"
              />
            </div>
            {activeTab === 'submissions' && (
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="job_linked">Job-Linked</SelectItem>
                  <SelectItem value="standalone">Standalone</SelectItem>
                  <SelectItem value="asset_linked">Asset-Linked</SelectItem>
                </SelectContent>
              </Select>
            )}
            {activeTab === 'templates' && isAdmin && hasCustomForms && (
              <Button onClick={() => handleOpenTemplateDialog()} className="btn-volt">
                <Plus className="h-4 w-4 mr-2" />
                New Template
              </Button>
            )}
          </div>
        </div>

        {/* Submissions Tab */}
        <TabsContent value="submissions" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead sortKey="form" requestSort={requestSubmissionSort} getSortDirection={getSubmissionSortDirection}>Form</SortableTableHead>
                    <SortableTableHead sortKey="operator" requestSort={requestSubmissionSort} getSortDirection={getSubmissionSortDirection}>Operator</SortableTableHead>
                    <SortableTableHead sortKey="asset" requestSort={requestSubmissionSort} getSortDirection={getSubmissionSortDirection}>Asset/Job</SortableTableHead>
                    <SortableTableHead sortKey="submitted" requestSort={requestSubmissionSort} getSortDirection={getSubmissionSortDirection}>Submitted</SortableTableHead>
                    <SortableTableHead sortKey="type" requestSort={requestSubmissionSort} getSortDirection={getSubmissionSortDirection}>Type</SortableTableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedSubmissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        {submissionsList.loading ? 'Loading submissions…' : 'No form submissions found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedSubmissions.map((sub) => {
                      const template = templates.find(t => t.id === sub.template_id);
                      return (
                        <TableRow key={sub.id} className="cursor-pointer hover:bg-muted/50" onClick={() => handleViewSubmission(sub)} data-testid={`submission-row-${sub.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2 flex-wrap">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{sub.template_name}</span>
                              {renderApprovalBadge(sub)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              {sub.operator_name}
                            </div>
                          </TableCell>
                          <TableCell>
                            {sub.equipment_name ? (
                              <div className="flex items-center gap-2">
                                <Truck className="h-4 w-4 text-muted-foreground" />
                                {sub.equipment_name}
                              </div>
                            ) : sub.job_description ? (
                              <span className="text-sm text-muted-foreground">{sub.job_description}</span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              {formatDate(sub.submitted_at)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              template?.form_type === 'standalone' 
                                ? 'bg-amber-500/20 text-amber-500' 
                                : template?.form_type === 'asset_linked'
                                ? 'bg-emerald-500/20 text-emerald-500'
                                : 'bg-blue-500/20 text-blue-500'
                            }>
                              {template?.form_type === 'standalone' ? 'Standalone' : template?.form_type === 'asset_linked' ? 'Asset-Linked' : 'Job-Linked'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewSubmission(sub)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              <div ref={submissionsList.sentinelRef} />
              {filteredSubmissions.length > 0 && (
                <div className="flex items-center justify-between pt-4 text-sm text-muted-foreground" data-testid="submissions-pagination-footer">
                  <span>Showing {filteredSubmissions.length} of {submissionsList.total}</span>
                  {submissionsList.hasMore && (
                    <Button size="sm" variant="outline" onClick={submissionsList.loadMore} data-testid="submissions-load-more">Load more</Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-4">
          {/* Upgrade prompt for Basic tier */}
          {!hasCustomForms && (
            <UpgradePrompt 
              feature="custom_forms"
              requiredTier="advanced"
              title="Custom Forms Builder"
              description="Create your own custom forms tailored to your business needs. Basic tier includes system forms for Pre-Start checks and Incident Reports."
              className="mb-4"
            />
          )}
          {isAdmin && inactiveCount > 0 && (
            <div className="flex items-center justify-end gap-2 mb-3" data-testid="forms-show-inactive-toggle">
              <Label htmlFor="show-inactive-forms" className="text-sm text-muted-foreground">
                Show inactive ({inactiveCount})
              </Label>
              <Switch id="show-inactive-forms" checked={showInactive} onCheckedChange={setShowInactive} />
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map((template) => (
              <Card key={template.id} className={`relative transition-opacity ${!template.is_active ? 'opacity-60' : ''}`} data-testid={`form-template-card-${template.id}`}>
                {template.is_system && (
                  <Badge className="absolute top-2 right-2 bg-[#ffd108] text-black">
                    System
                  </Badge>
                )}
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {template.name}
                  </CardTitle>
                  <CardDescription>{template.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Type:</span>
                      <Badge variant="outline">
                        {template.form_type === 'standalone' ? 'Standalone' : template.form_type === 'asset_linked' ? 'Asset-Linked' : 'Job-Linked'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Fields:</span>
                      <span>{template.fields?.length || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Status:</span>
                      {isAdmin ? (
                        <div className="flex items-center gap-2" data-testid={`form-template-status-${template.id}`}>
                          <Badge variant={template.is_active ? 'default' : 'secondary'}>
                            {template.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Switch
                            checked={template.is_active}
                            onCheckedChange={() => handleToggleActive(template)}
                            data-testid={`form-template-active-toggle-${template.id}`}
                            aria-label={template.is_active ? 'Deactivate form' : 'Activate form'}
                          />
                        </div>
                      ) : (
                        <Badge variant={template.is_active ? 'default' : 'secondary'}>
                          {template.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      )}
                    </div>
                    {template.form_type !== 'standalone' && (template.is_mandatory || template.include_in_docket || template.allow_multiple_submissions === false) && (
                      <div className="flex flex-wrap gap-1 pt-1">
                        {template.is_mandatory && <Badge variant="outline" className="text-[10px] border-red-500/50 text-red-500">Mandatory</Badge>}
                        {template.include_in_docket && <Badge variant="outline" className="text-[10px] border-blue-500/50 text-blue-500">In Docket PDF</Badge>}
                        {template.allow_multiple_submissions === false && <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-500">Single Submit</Badge>}
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t flex justify-end gap-2">
                    {/* Edit button - available for all templates */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenTemplateDialog(template)}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    {isAdmin && hasCustomForms && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDuplicateTemplate(template)}
                        data-testid={`duplicate-template-${template.id}`}
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Duplicate
                      </Button>
                    )}
                    {/* Delete button - only for non-system templates and Advanced+ tiers */}
                    {isAdmin && !template.is_system && hasCustomForms && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteTarget(template)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-template-${template.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Hire Inspections Tab */}
        <TabsContent value="hire-inspections" className="mt-4">
          <HireInspectionTemplatesTab />
        </TabsContent>
      </Tabs>

      {/* View Submission Sheet (Flyout Panel) */}
      <Sheet open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <div className="flex items-center justify-between gap-2">
              <SheetTitle className="font-heading text-xl">{selectedSubmission?.template_name}</SheetTitle>
              {canEditSubmission() && !editingSubmission && (
                <Button size="sm" variant="outline" onClick={startEditSubmission} data-testid="edit-submission-btn">
                  <Pencil className="h-4 w-4 mr-1" /> Edit
                </Button>
              )}
            </div>
            {selectedSubmission?.edit_count > 0 && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <History className="h-3 w-3" /> Edited {selectedSubmission.edit_count} time{selectedSubmission.edit_count > 1 ? 's' : ''}
                {selectedSubmission.last_edited_by_name ? ` · last by ${selectedSubmission.last_edited_by_name}` : ''}
                {selectedSubmission.last_edited_at ? ` · ${formatDate(selectedSubmission.last_edited_at)}` : ''}
              </p>
            )}
          </SheetHeader>
          {selectedSubmission && (
            <div className="space-y-4 py-4">
              {/* Approval gate status (Phase 4) */}
              {selectedSubmission.requires_approval && (
                <div className="rounded-lg border p-4 space-y-3" data-testid="submission-approval-panel">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <AlertTriangle className="h-4 w-4 text-amber-500" /> Supervisor Approval
                    </span>
                    {renderApprovalBadge(selectedSubmission)}
                  </div>
                  {selectedSubmission.approval_status === 'approved' && (
                    <p className="text-xs text-muted-foreground">
                      Approved by {selectedSubmission.approved_by_name || 'supervisor'} · {formatDate(selectedSubmission.approved_at)}
                    </p>
                  )}
                  {selectedSubmission.approval_status === 'rejected' && (
                    <p className="text-xs text-red-500">
                      Rejected by {selectedSubmission.approved_by_name || 'supervisor'} · {formatDate(selectedSubmission.approved_at)}
                      {selectedSubmission.rejection_reason ? ` — "${selectedSubmission.rejection_reason}"` : ''}
                    </p>
                  )}
                  {isAdmin && selectedSubmission.approval_status === 'pending' && (
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleApproveSubmission(selectedSubmission)} data-testid="submission-approve-btn">
                        <CheckCircle className="h-4 w-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleRejectSubmission(selectedSubmission)} data-testid="submission-reject-btn">
                        <Trash2 className="h-4 w-4 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {/* Submission Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">Operator</p>
                    <p className="font-medium">{selectedSubmission.operator_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Submitted</p>
                    <p className="font-medium">{formatDate(selectedSubmission.submitted_at)}</p>
                  </div>
                  {selectedSubmission.equipment_name && (
                    <div>
                      <p className="text-xs text-muted-foreground">Asset</p>
                      <p className="font-medium">{selectedSubmission.equipment_name}</p>
                    </div>
                  )}
                  {selectedSubmission.job_description && (
                    <div>
                      <p className="text-xs text-muted-foreground">Job</p>
                      <p className="font-medium">{selectedSubmission.job_description}</p>
                    </div>
                  )}
                </div>

                {/* Form Responses */}
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Responses
                  </h4>
                  {editingSubmission ? (
                    <>
                      {(selectedSubmission.template?.fields || [])
                        .filter((field) => field.type !== 'section')
                        .map((field) => (
                          <div key={field.id} className="py-2 border-b space-y-1.5">
                            <span className="text-sm font-medium">{field.label}</span>
                            <div>
                              {renderFieldInput(field, editData[field.id], (val) =>
                                setEditData((prev) => ({ ...prev, [field.id]: val }))
                              )}
                            </div>
                          </div>
                        ))}
                      <div className="space-y-1.5 pt-2">
                        <Label className="text-xs">Reason for edit (optional, logged)</Label>
                        <Textarea
                          value={editSubNote}
                          onChange={(e) => setEditSubNote(e.target.value)}
                          placeholder="e.g. Corrected the hours recorded after checking with the operator"
                          data-testid="edit-submission-note"
                        />
                      </div>
                    </>
                  ) : (
                    (selectedSubmission.template?.fields || []).map((field) => (
                      <div key={field.id} className="flex justify-between items-start py-2 border-b">
                        <span className="text-sm font-medium">{field.label}</span>
                        <span className="text-sm text-right">
                          {renderFieldValue(field, selectedSubmission.data?.[field.id])}
                        </span>
                      </div>
                    ))
                  )}
                </div>

                {/* Signature */}
                {selectedSubmission.signature_data && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                      Signature
                    </h4>
                    <img 
                      src={selectedSubmission.signature_data} 
                      alt="Signature" 
                      className="border rounded-lg max-w-xs"
                    />
                  </div>
                )}

                {/* Photos */}
                {selectedSubmission.photos?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                      Photos
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedSubmission.photos.map((photo, idx) => (
                        <button key={`photo-${idx}`} type="button" onClick={() => setLightboxUrl(photo)} className="cursor-zoom-in">
                          <img src={photo} alt={`Photo ${idx + 1}`} className="rounded-lg border hover:opacity-80" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Edit History (audit trail) */}
                {!editingSubmission && Array.isArray(selectedSubmission.edit_history) && selectedSubmission.edit_history.length > 0 && (
                  <div className="space-y-2" data-testid="submission-edit-history">
                    <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                      <History className="h-4 w-4" /> Edit History
                    </h4>
                    <div className="space-y-2">
                      {[...selectedSubmission.edit_history].reverse().map((entry, idx) => (
                        <div key={`edit-${idx}`} className="rounded-lg border p-3 text-xs space-y-1.5 bg-muted/30">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{entry.edited_by_name || 'Admin'}</span>
                            <span className="text-muted-foreground">{formatDate(entry.edited_at)}</span>
                          </div>
                          {entry.note && <p className="italic text-muted-foreground">"{entry.note}"</p>}
                          <ul className="space-y-0.5">
                            {(entry.changes || []).map((c, ci) => (
                              <li key={`chg-${ci}`} className="flex flex-wrap gap-1">
                                <span className="font-medium">{c.label}:</span>
                                <span className="text-red-500 line-through">{c.old}</span>
                                <CornerDownRight className="h-3 w-3 text-muted-foreground" />
                                <span className="text-green-600">{c.new}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
          <SheetFooter className="pt-4 border-t mt-6">
            {editingSubmission ? (
              <>
                <Button variant="outline" onClick={() => setEditingSubmission(false)} disabled={savingEdit} data-testid="cancel-edit-submission-btn">Cancel</Button>
                <Button className="btn-volt" onClick={saveSubmissionEdit} disabled={savingEdit} data-testid="save-edit-submission-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
                <Button className="btn-volt" onClick={() => selectedSubmission && handleDownloadSubmissionPDF(selectedSubmission)} data-testid="download-submission-pdf-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              </>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Template Editor Sheet (Flyout Panel) */}
      <Sheet open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl">
              {selectedTemplate ? 'Edit Template' : 'Create Template'}
            </SheetTitle>
            {selectedTemplate?.is_system && (
              <p className="text-sm text-muted-foreground">System template - you can edit fields but cannot rename</p>
            )}
          </SheetHeader>
          <form onSubmit={handleSaveTemplate} className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Template Name</Label>
                    <Input
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                      placeholder="e.g., Daily Inspection"
                      required
                      disabled={selectedTemplate?.is_system}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Form Type</Label>
                    <Select
                      value={templateForm.form_type}
                      onValueChange={(v) => setTemplateForm({ ...templateForm, form_type: v })}
                      disabled={selectedTemplate?.is_system}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="job_linked">Job-Linked</SelectItem>
                        <SelectItem value="standalone">Standalone</SelectItem>
                        <SelectItem value="asset_linked">Asset-Linked</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                    placeholder="Brief description of this form..."
                    disabled={selectedTemplate?.is_system}
                  />
                </div>

                {/* General options (all form types) */}
                <div className="flex items-center justify-between gap-2 p-3 rounded-lg border bg-background" data-testid="template-allow-edit-block">
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-1.5">
                      <Pencil className="h-4 w-4 text-muted-foreground" /> Allow Edit After Submission
                    </Label>
                    <p className="text-xs text-muted-foreground">Let admins correct a submission after it's sent. Every change is logged for audit.</p>
                  </div>
                  <Switch
                    checked={templateForm.allow_edit_after_submission}
                    onCheckedChange={(v) => setTemplateForm({ ...templateForm, allow_edit_after_submission: v })}
                    disabled={selectedTemplate?.is_system}
                    data-testid="template-allow-edit-toggle"
                  />
                </div>

                {/* Job/Asset-linked form options */}
                {templateForm.form_type !== 'standalone' && (
                  <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
                    <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">{templateForm.form_type === 'asset_linked' ? 'Asset-Linked Options' : 'Job-Linked Options'}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="flex items-center justify-between gap-2 p-3 rounded-lg border bg-background">
                        <div>
                          <Label className="text-sm font-medium">Mandatory</Label>
                          <p className="text-xs text-muted-foreground">Required before docket</p>
                        </div>
                        <Switch
                          checked={templateForm.is_mandatory}
                          onCheckedChange={(v) => setTemplateForm({ ...templateForm, is_mandatory: v })}
                          data-testid="template-mandatory-toggle"
                        />
                      </div>
                      <div className="flex items-center justify-between gap-2 p-3 rounded-lg border bg-background">
                        <div>
                          <Label className="text-sm font-medium">Add to Docket PDF</Label>
                          <p className="text-xs text-muted-foreground">Include in docket export</p>
                        </div>
                        <Switch
                          checked={templateForm.include_in_docket}
                          onCheckedChange={(v) => setTemplateForm({ ...templateForm, include_in_docket: v })}
                          data-testid="template-include-docket-toggle"
                        />
                      </div>
                      <div className="flex items-center justify-between gap-2 p-3 rounded-lg border bg-background">
                        <div>
                          <Label className="text-sm font-medium">Multiple Submissions</Label>
                          <p className="text-xs text-muted-foreground">Allow per job</p>
                        </div>
                        <Switch
                          checked={templateForm.allow_multiple_submissions}
                          onCheckedChange={(v) => setTemplateForm({ ...templateForm, allow_multiple_submissions: v })}
                          data-testid="template-multi-submit-toggle"
                        />
                      </div>
                    </div>

                    {/* Equipment Types Filter */}
                    <div className="space-y-2">
                      <Label className="text-sm">Restrict to Asset Types <span className="text-muted-foreground font-normal">(optional)</span></Label>
                      <p className="text-xs text-muted-foreground">Leave empty to show for all jobs. Select types to restrict this form to jobs with matching assets.</p>
                      <div className="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1 bg-background">
                        {equipmentTypesList.length === 0 ? (
                          <p className="text-xs text-muted-foreground p-2">No asset types configured</p>
                        ) : (
                          equipmentTypesList.map(eqName => {
                            const checked = (templateForm.equipment_types || []).includes(eqName);
                            return (
                              <label key={eqName} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm hover:bg-muted/50 ${checked ? 'bg-muted font-medium' : 'text-foreground'}`}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    const current = templateForm.equipment_types || [];
                                    setTemplateForm({
                                      ...templateForm,
                                      equipment_types: e.target.checked
                                        ? [...current, eqName]
                                        : current.filter(t => t !== eqName)
                                    });
                                  }}
                                  className="accent-[#ffd108]"
                                />
                                <span className="text-gray-900 dark:text-gray-100">{eqName}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      {(templateForm.equipment_types || []).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {templateForm.equipment_types.map(t => (
                            <Badge key={t} variant="secondary" className="text-xs">
                              {t}
                              <button
                                type="button"
                                className="ml-1 hover:text-destructive"
                                onClick={() => setTemplateForm({
                                  ...templateForm,
                                  equipment_types: templateForm.equipment_types.filter(x => x !== t)
                                })}
                              >×</button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Risk-based approval gate (Phase 4) */}
                    <div className="space-y-3 border-t pt-4" data-testid="approval-gate-section">
                      <div className="flex items-center justify-between gap-2 p-3 rounded-lg border bg-background">
                        <div>
                          <Label className="text-sm font-medium flex items-center gap-1.5">
                            <AlertTriangle className="h-4 w-4 text-amber-500" /> Requires Supervisor Approval
                          </Label>
                          <p className="text-xs text-muted-foreground">Hold submissions for sign-off before work can proceed</p>
                        </div>
                        <Switch
                          checked={templateForm.requires_approval}
                          onCheckedChange={(v) => setTemplateForm({ ...templateForm, requires_approval: v })}
                          data-testid="template-requires-approval-toggle"
                        />
                      </div>

                      {templateForm.requires_approval && (
                        <div className="space-y-3 pl-1">
                          <div className="space-y-1">
                            <Label className="text-xs">Message shown to worker (optional)</Label>
                            <Textarea
                              value={templateForm.approval_message || ''}
                              onChange={(e) => setTemplateForm({ ...templateForm, approval_message: e.target.value })}
                              placeholder="e.g. High-risk work detected — a supervisor must review this JHA before you start."
                              rows={2}
                              data-testid="template-approval-message"
                            />
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs">Trigger approval when…</Label>
                              <Button type="button" variant="outline" size="sm" onClick={addApprovalRule} data-testid="add-approval-rule-btn">
                                <Plus className="h-3 w-3 mr-1" />Add condition
                              </Button>
                            </div>
                            <p className="text-[11px] text-muted-foreground">
                              {(templateForm.approval_rules || []).length === 0
                                ? 'No conditions — every submission of this form will require approval.'
                                : 'Approval is required when ANY of these conditions match the answers (risk-based).'}
                            </p>
                            {(templateForm.approval_rules || []).map((rule, ri) => {
                              const answerFields = (templateForm.fields || []).filter(
                                f => f.label && !['section', 'section_header', 'signature', 'photo', 'multi_photo', 'repeating_group'].includes(f.type)
                              );
                              const noValueOps = ['is_yes', 'is_no', 'not_empty', 'is_empty'];
                              return (
                                <div key={ri} className="grid grid-cols-12 gap-2 items-center" data-testid={`approval-rule-${ri}`}>
                                  <div className="col-span-4">
                                    <Select value={rule.field || ''} onValueChange={(v) => updateApprovalRule(ri, { field: v })}>
                                      <SelectTrigger className="h-8 text-xs" data-testid={`approval-rule-field-${ri}`}><SelectValue placeholder="Field" /></SelectTrigger>
                                      <SelectContent>
                                        {answerFields.map(f => <SelectItem key={f.id} value={f.id}>{f.label || 'Untitled'}</SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="col-span-4">
                                    <Select value={rule.op || 'equals'} onValueChange={(v) => updateApprovalRule(ri, { op: v })}>
                                      <SelectTrigger className="h-8 text-xs" data-testid={`approval-rule-op-${ri}`}><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        {CONDITION_OPERATORS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="col-span-3">
                                    {!noValueOps.includes(rule.op) && (
                                      <Input
                                        className="h-8 text-xs"
                                        value={rule.value || ''}
                                        placeholder="Value"
                                        onChange={(e) => updateApprovalRule(ri, { value: e.target.value })}
                                        data-testid={`approval-rule-value-${ri}`}
                                      />
                                    )}
                                  </div>
                                  <Button type="button" variant="ghost" size="sm" className="col-span-1 text-destructive h-8 w-8 p-0" onClick={() => removeApprovalRule(ri)} data-testid={`remove-approval-rule-${ri}`}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Form Fields</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addField}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Field
                    </Button>
                  </div>

                  {templateForm.fields.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed rounded-lg">
                      <p className="text-muted-foreground">No fields added yet</p>
                      <p className="text-sm text-muted-foreground">Click "Add Field" to start building your form</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {templateForm.fields.map((field, index) => (
                        <div
                          key={field.id}
                          className={`p-3 border rounded-lg space-y-3 transition-colors ${dragOverFieldIndex === index ? 'border-[#ffd108] border-dashed bg-[#ffd108]/5' : ''} ${dragFieldIndex === index ? 'opacity-50' : ''}`}
                          onDragOver={(e) => { e.preventDefault(); if (dragFieldIndex !== null && dragOverFieldIndex !== index) setDragOverFieldIndex(index); }}
                          onDragLeave={() => { if (dragOverFieldIndex === index) setDragOverFieldIndex(null); }}
                          onDrop={(e) => { e.preventDefault(); handleFieldDrop(index); }}
                          data-testid={`form-field-card-${index}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1">
                              <span
                                draggable
                                onDragStart={() => setDragFieldIndex(index)}
                                onDragEnd={() => { setDragFieldIndex(null); setDragOverFieldIndex(null); }}
                                className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-1 -ml-1"
                                title="Drag to reorder"
                                data-testid={`field-drag-handle-${index}`}
                              >
                                <GripVertical className="h-4 w-4" />
                              </span>
                              <span className="text-sm font-medium">Field {index + 1}</span>
                            </div>
                            <div className="flex items-center gap-0.5">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => moveField(index, -1)}
                                disabled={index === 0}
                                className="h-8 w-8 p-0"
                                title="Move up"
                                data-testid={`field-move-up-${index}`}
                              >
                                <ArrowUp className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => moveField(index, 1)}
                                disabled={index === templateForm.fields.length - 1}
                                className="h-8 w-8 p-0"
                                title="Move down"
                                data-testid={`field-move-down-${index}`}
                              >
                                <ArrowDown className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => insertFieldAfter(index)}
                                className="h-8 w-8 p-0 text-[#0891b2] dark:text-[#22d3ee]"
                                title="Insert field below"
                                data-testid={`field-insert-below-${index}`}
                              >
                                <CornerDownRight className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => duplicateField(index)}
                                className="h-8 w-8 p-0"
                                title="Duplicate field"
                                data-testid={`field-duplicate-${index}`}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeField(index)}
                                className="text-destructive h-8 w-8 p-0"
                                title="Delete field"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Label</Label>
                              <Input
                                value={field.label}
                                onChange={(e) => updateField(index, { label: e.target.value })}
                                placeholder="Field label"
                                required
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Type</Label>
                              <Select
                                value={field.type}
                                onValueChange={(v) => updateField(index, { type: v })}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="text">Text</SelectItem>
                                  <SelectItem value="number">Number</SelectItem>
                                  <SelectItem value="textarea">Long Text</SelectItem>
                                  <SelectItem value="yes_no">Yes/No</SelectItem>
                                  <SelectItem value="checkbox">Checkbox</SelectItem>
                                  <SelectItem value="select">Dropdown</SelectItem>
                                  <SelectItem value="multi_select">Checklist (multi-select)</SelectItem>
                                  <SelectItem value="date">Date</SelectItem>
                                  <SelectItem value="signature">Signature</SelectItem>
                                  <SelectItem value="photo">Photo</SelectItem>
                                  <SelectItem value="multi_photo">Multiple Photos</SelectItem>
                                  <SelectItem value="section_header">Section Heading / Divider</SelectItem>
                                  <SelectItem value="section">Section / Info + Image</SelectItem>
                                  <SelectItem value="repeating_group">Repeating Table</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          {(field.type === 'select' || field.type === 'multi_select') && (
                            <div className="space-y-1">
                              <Label className="text-xs">Options (comma separated)</Label>
                              <Input
                                defaultValue={field.options?.join(', ') || ''}
                                onBlur={(e) => updateField(index, { 
                                  options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) 
                                })}
                                placeholder="Option 1, Option 2, Option 3"
                              />
                            </div>
                          )}
                          {field.type === 'section_header' && (
                            <div className="space-y-1 border-l-2 border-[#ffd108]/60 pl-3" data-testid={`section-header-editor-${index}`}>
                              <Label className="text-xs">Subtitle (optional)</Label>
                              <Input
                                value={field.description || ''}
                                onChange={(e) => updateField(index, { description: e.target.value })}
                                placeholder="Optional smaller text under the heading"
                              />
                              <p className="text-[11px] text-muted-foreground">Shown as a bold heading with a divider to group the questions that follow. The "Label" above is the heading text.</p>
                            </div>
                          )}
                          {field.type === 'section' && (
                            <div className="space-y-2 border-l-2 border-primary/40 pl-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Description / instructions (optional)</Label>
                                <Textarea
                                  value={field.description || ''}
                                  onChange={(e) => updateField(index, { description: e.target.value })}
                                  placeholder="Informational text shown to the worker under the heading"
                                  rows={2}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Informational image (optional)</Label>
                                {field.image_url && (
                                  <img src={field.image_url} alt="section" className="max-h-40 rounded border mb-2" />
                                )}
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    className="text-xs"
                                    data-testid={`section-image-upload-${index}`}
                                    onChange={(e) => handleSectionImageUpload(index, e.target.files?.[0])}
                                  />
                                  {field.image_url && (
                                    <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={() => updateField(index, { image_url: null })}>
                                      Remove
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                          {field.type === 'repeating_group' && (
                            <div className="space-y-2 border-l-2 border-primary/40 pl-3">
                              <div className="flex items-center justify-between">
                                <Label className="text-xs">Table columns</Label>
                                <Button type="button" variant="outline" size="sm" onClick={() => addColumn(index)} data-testid={`add-column-${index}`}>
                                  <Plus className="h-3 w-3 mr-1" />Add Column
                                </Button>
                              </div>
                              {(field.columns || []).length === 0 && (
                                <p className="text-xs text-muted-foreground">No columns yet — add at least one.</p>
                              )}
                              {(field.columns || []).map((col, ci) => (
                                <div key={col.id || ci} className="grid grid-cols-12 gap-2 items-start">
                                  <Input
                                    className="col-span-5 h-8 text-xs"
                                    value={col.label || ''}
                                    placeholder="Column label"
                                    onChange={(e) => updateColumn(index, ci, { label: e.target.value })}
                                    data-testid={`column-label-${index}-${ci}`}
                                  />
                                  <div className="col-span-5">
                                    <Select value={col.type || 'text'} onValueChange={(v) => updateColumn(index, ci, { type: v })}>
                                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="text">Text</SelectItem>
                                        <SelectItem value="number">Number</SelectItem>
                                        <SelectItem value="date">Date</SelectItem>
                                        <SelectItem value="select">Dropdown</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    {col.type === 'select' && (
                                      <Input
                                        className="h-8 text-xs mt-1"
                                        value={col.options?.join(', ') || ''}
                                        placeholder="Options: Low, Medium, High"
                                        onChange={(e) => updateColumn(index, ci, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                                      />
                                    )}
                                  </div>
                                  <Button type="button" variant="ghost" size="sm" className="col-span-2 text-destructive h-8" onClick={() => removeColumn(index, ci)}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                          {!['section', 'section_header'].includes(field.type) && (
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={field.required}
                              onCheckedChange={(v) => updateField(index, { required: v })}
                            />
                            <Label className="text-xs">Required field</Label>
                          </div>
                          )}

                          {/* Conditional visibility (Phase 3) */}
                          {(() => {
                            const priorFields = templateForm.fields
                              .slice(0, index)
                              .filter(f => f.label && !['section', 'section_header', 'signature', 'photo', 'multi_photo', 'repeating_group'].includes(f.type));
                            const si = field.show_if || null;
                            const noValueOps = ['is_yes', 'is_no', 'not_empty', 'is_empty'];
                            return (
                              <div className="space-y-2 border-t pt-2" data-testid={`field-conditional-${index}`}>
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={!!si}
                                    disabled={priorFields.length === 0}
                                    onCheckedChange={(v) => setFieldShowIf(index, v ? { field: priorFields[0]?.id || '', op: 'equals', value: '' } : null)}
                                    data-testid={`field-conditional-toggle-${index}`}
                                  />
                                  <Label className="text-xs">Only show this field when…</Label>
                                  {priorFields.length === 0 && <span className="text-[10px] text-muted-foreground">(add a field above first)</span>}
                                </div>
                                {si && (
                                  <div className="grid grid-cols-12 gap-2 items-center">
                                    <div className="col-span-4">
                                      <Select value={si.field || ''} onValueChange={(v) => setFieldShowIf(index, { ...si, field: v })}>
                                        <SelectTrigger className="h-8 text-xs" data-testid={`field-conditional-field-${index}`}><SelectValue placeholder="Field" /></SelectTrigger>
                                        <SelectContent>
                                          {priorFields.map(f => <SelectItem key={f.id} value={f.id}>{f.label || 'Untitled'}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="col-span-4">
                                      <Select value={si.op || 'equals'} onValueChange={(v) => setFieldShowIf(index, { ...si, op: v })}>
                                        <SelectTrigger className="h-8 text-xs" data-testid={`field-conditional-op-${index}`}><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          {CONDITION_OPERATORS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="col-span-4">
                                      {!noValueOps.includes(si.op) && (
                                        <Input
                                          className="h-8 text-xs"
                                          value={si.value || ''}
                                          placeholder="Value"
                                          onChange={(e) => setFieldShowIf(index, { ...si, value: e.target.value })}
                                          data-testid={`field-conditional-value-${index}`}
                                        />
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
            <SheetFooter className="pt-4 border-t mt-6">
              <Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" data-testid="template-save-btn">
                {selectedTemplate ? 'Update Template' : 'Create Template'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete Template Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-template">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteTemplate(deleteTarget?.id)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-template"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
      </div>
  );
};

export default Forms;
